import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach bearer token from localStorage as fallback (some browsers reject 3rd-party cookies)
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("sb_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ---------------------------------------------------------------------------
// F1+F2+F3 · Single-flight refresh su 401 (M2.3.1)
// ---------------------------------------------------------------------------
// Quando l'access token scade (8h), il primo 401 innesca UNA SOLA chiamata
// a /api/auth/refresh (via refresh_token cookie httpOnly). Al successo:
//   - aggiorna sb_token in localStorage (fallback bearer)
//   - retry TRASPARENTE della/e request originali (max 1 volta)
// Se il refresh fallisce (401 dal refresh) → la sessione è realmente scaduta.
// In quel caso puliamo sb_token e propaghiamo il 401.
//
// Errori network/timeout/5xx NON sono trattati come logout: il retry esistente
// (solo GET) resta invariato; per gli altri metodi l'errore torna al chiamante
// così può mostrare "Errore di rete" senza cadere in login.
let _refreshPromise = null;
function _dispatchAuthLost() {
  try { window.dispatchEvent(new CustomEvent("sb-auth-lost")); } catch { /* noop */ }
}
async function _doRefresh() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const resp = await axios.post(`${API_BASE}/auth/refresh`, {}, {
        withCredentials: true,
        // Fallback bearer se cookie 3rd-party bloccati: usa sb_token corrente
        // (che a sua volta è un access token scaduto — ma il backend accetta
        // Authorization: Bearer <refresh_token> come fallback, vedi routes/auth.py).
        // Nota: se il cookie refresh_token c'è, il backend lo preferisce.
      });
      const newToken = resp?.data?.access_token;
      if (newToken) localStorage.setItem("sb_token", newToken);
      return newToken || true;
    } catch (e) {
      // 401 dal refresh → sessione realmente persa
      if (e?.response?.status === 401) {
        localStorage.removeItem("sb_token");
        _dispatchAuthLost();
      }
      throw e;
    } finally {
      // Reset dopo un breve tick: se più 401 arrivano quasi insieme, riusano
      // la stessa promise; dopo il tick il refresh è concluso e prossimi 401
      // (in un secondo, dopo che l'access è scaduto di nuovo) faranno un
      // nuovo refresh.
      setTimeout(() => { _refreshPromise = null; }, 100);
    }
  })();
  return _refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err?.config || {};
    const status = err?.response?.status;
    const url = cfg.url || "";
    // NON retryare loop: /auth/refresh, /auth/login, /auth/pin-login
    const isAuthEndpoint = /\/auth\/(refresh|login|pin-login|pin-logout)/.test(url);
    if (status !== 401 || isAuthEndpoint || cfg.__sbRefreshTried) {
      return Promise.reject(err);
    }
    cfg.__sbRefreshTried = true;
    try {
      await _doRefresh();
      return api.request(cfg);
    } catch (refreshErr) {
      // Sessione non recuperabile → propaga 401 originale
      return Promise.reject(err);
    }
  }
);

// Fase 10 — dispatch a global event whenever the admin mutates a menu-related
// entity, so the MenuPublishBar refreshes its status without waiting for the
// polling tick. Excludes /admin/menu/publish|discard|status|preview themselves.
const MENU_MUTATION_RE = /\/admin\/(categories|products|modifier-groups|menus)(\b|\/)/;
const MENU_META_RE = /\/admin\/menu\/(status|preview|versions)/;
api.interceptors.response.use((res) => {
  try {
    const method = (res.config.method || "").toLowerCase();
    const url = res.config.url || "";
    if (["post", "patch", "put", "delete"].includes(method)
        && MENU_MUTATION_RE.test(url)
        && !MENU_META_RE.test(url)) {
      window.dispatchEvent(new CustomEvent("sb-menu-changed"));
    }
  } catch { /* noop */ }
  return res;
});

// Sprint 0.2 — Retry silenzioso su GET (Beach Menu PRD v1.1).
// Ogni GET /public/* (o simili) fallita per rete/5xx/429 viene ritentata
// automaticamente 3 volte con backoff (500ms, 1.5s, 4s) PRIMA che il
// chiamante veda l'errore. Solo GET (idempotenti) — mai POST/PATCH.
const _RETRY_DELAYS_MS = [500, 1500, 4000];

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err?.config || {};
    const method = (config.method || "").toLowerCase();
    if (method !== "get") return Promise.reject(err);
    const status = err?.response?.status;
    const isNetwork = !err?.response;
    const isRetryable = isNetwork || status === 429 || (status >= 500 && status < 600);
    if (!isRetryable) return Promise.reject(err);

    config.__sbRetry = (config.__sbRetry ?? -1) + 1;
    if (config.__sbRetry >= _RETRY_DELAYS_MS.length) return Promise.reject(err);
    await new Promise((r) => setTimeout(r, _RETRY_DELAYS_MS[config.__sbRetry]));
    return api.request(config);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Errore imprevisto";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

export function eur(cents) {
  const n = (cents || 0) / 100;
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

export function stationLabel(s) {
  if (!s) return "";
  const type = { umbrella: "Ombrellone", table: "Tavolo", sunbed: "Lettino", vip: "VIP", other: "Postazione" }[s.type] || "Postazione";
  return `${type} ${s.name}`;
}
